import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Field, change } from 'redux-form';
import Table from 'react-table';
import selectTableHOC from 'react-table/lib/hoc/selectTable';
import 'react-table/react-table.css';
import '../../../../config/columns.css';
import TextInput from '../../forms/input/TextInput';
import {
  COLOR,
  DATE_STRING_LENGTH,
  DEFAULT_ORDER,
  DEFAULT_PAGE_SIZE,
  DEFAULT_SORT,
} from '../../../../config/constants';
import * as ColumnFilterDataTypes from '../../../../config/columnFilterDataTypes';

const hasValidArrayColumnFilter = (value) => value.length > 0;

const hasValidDateColumnFilter = (value) => value.length === DATE_STRING_LENGTH;

const SelectionableTableHOC = selectTableHOC(Table);

class SelectionableTable extends Component {
  static isRequired = (value) =>
    value ? undefined : 'Debe seleccionar al menos un elemento de la tabla';

  static areSetsEqual(setA, setB) {
    return setA.size === setB.size && [...setA].every((item) => setB.has(item));
  }

  constructor(props) {
    super(props);

    this.state = {
      selectAll: false,
      selection: [],
      allElements: [],
    };
  }

  componentDidMount() {
    this.initializeSelection();
    this.attachEnterBlocker();
  }

  componentDidUpdate(prevProps) {
    const prevIds = new Set(prevProps.data.map((item) => item.id));
    const currentIds = new Set(this.props.data.map((item) => item.id));

    const dataChanged = !SelectionableTable.areSetsEqual(prevIds, currentIds);

    const preSelectedIdsChanged =
      JSON.stringify(prevProps.preSelectedIds) !==
      JSON.stringify(this.props.preSelectedIds);

    if (dataChanged) {
      if (this.props.shouldResetSelection) {
        // reset all selection
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({ selection: [], selectAll: false });
      } else {
        // re-initialize selection based on new data
        this.initializeSelection();
      }
    } else if (preSelectedIdsChanged) {
      // only re-initialize if preSelectedIds changed AND data did NOT change
      this.initializeSelection();
    }
  }

  componentWillUnmount() {
    const { form, dispatchChange, name } = this.props;
    dispatchChange(form, name, null);
    if (this.attachedFormEl && this.blockEnterHandler) {
      this.attachedFormEl.removeEventListener(
        'keydown',
        this.blockEnterHandler,
        {
          capture: true,
        },
      );
      this.attachedFormEl = null;
      this.blockEnterHandler = null;
    }
  }

  onFetchData = (state, whenPressEnterKey = false) => {
    const { fetchData, params, queryProps } = this.props;

    const { page, pageSize, sorted, filtered } = state;

    let sort;
    sort = sorted.map(({ id, desc }) => {
      const order = desc ? 'desc' : 'asc';
      return `${id},${order}`;
    });
    if (sort && !sort.length > 0) {
      sort = DEFAULT_SORT;
    }

    let validFetchData = true;

    let query = [];

    if (whenPressEnterKey) {
      ({ query, validFetchData } = this.generateQueryToFetchData(
        state,
        whenPressEnterKey,
      ));
    } else {
      if (filtered.length !== 0) {
        validFetchData = false;
      }

      if (filtered.length === 0) {
        if (sorted.length !== 0) {
          validFetchData = true;
        }
      } else {
        ({ query, validFetchData } = this.generateQueryToFetchData(
          state,
          whenPressEnterKey,
        ));
      }
    }
    if (queryProps !== '') {
      query.push(queryProps);
    }

    if (validFetchData && fetchData) {
      this.setState({ selection: [], selectAll: false });

      fetchData({
        ...params,
        page: page || 0,
        sort: sort || DEFAULT_SORT,
        size: pageSize || DEFAULT_PAGE_SIZE,
        order: DEFAULT_ORDER,
        query,
      });
    }
  };

  /**
   * method public to get the current selection
   */
  getSelection = () => this.state.selection;

  attachEnterBlocker = () => {
    const { blockEnterSubmit } = this.props;
    if (!blockEnterSubmit) return;

    const node = this.wrapperRef;
    if (!node) return;

    const formEl = node.closest('form');
    if (!formEl) return;

    this.blockEnterHandler = (e) => {
      if (e.key !== 'Enter') return;
      const { target } = e;
      if (!(target instanceof HTMLElement)) return;
      // only events originated inside THIS table
      if (!node.contains(target)) return;

      const inPageJump = !!target.closest('.-pageJump');

      if (inPageJump && target instanceof HTMLInputElement) {
        // pagination: cancel implicit submit and force blur so react-table can commit page change
        e.preventDefault();
        target.blur();
        return;
      }

      if (!target.closest('.-pagination')) {
        // Filter and rest of the table: only cancel implicit submit
        e.preventDefault();
      }
    };

    formEl.addEventListener('keydown', this.blockEnterHandler, {
      capture: true,
    });
    this.attachedFormEl = formEl;
  };

  initializeSelection = () => {
    const {
      data,
      keyField,
      returnOnlySelectedItems,
      dispatchChange,
      form,
      activeField,
      name,
      preSelectedIds,
    } = this.props;

    // only proceed if there is data
    if (!data || data.length === 0) {
      return;
    }

    if (!returnOnlySelectedItems) {
      const allElements = [];
      const selection = [];

      data.forEach((element) => {
        let isPreSelected = false;

        if (preSelectedIds && preSelectedIds.length > 0) {
          // option 1: pre-selected IDs passed as prop
          isPreSelected = preSelectedIds.includes(element[keyField]);
        } else if (activeField && element[activeField] !== undefined) {
          // option 2: boolean field in the object (already existed)
          isPreSelected = element[activeField];
        }

        allElements.push({
          id: element[keyField],
          checked: isPreSelected,
          ...(this.props.returnFullObject ? { original: element } : {}),
        });

        if (isPreSelected) {
          selection.push(element[keyField]);
        }
      });

      this.setState({
        allElements,
        selection,
      });

      dispatchChange(form, name, allElements);
    } else {
      // only return selected items (IDs)
      const selection = [];

      if (preSelectedIds && preSelectedIds.length > 0) {
        selection.push(...preSelectedIds);
      } else if (activeField) {
        data.forEach((element) => {
          if (element[activeField]) {
            selection.push(element[keyField]);
          }
        });
      }

      this.setState({
        selection,
      });

      const value = this.mergeRecords(selection);

      dispatchChange(form, name, value.length > 0 ? value : null);

    }
  };

  /**
   * Toggle a single checkbox for select table
   */
  toggleSelection = (row) => {
    const {
      form,
      dispatchChange,
      name,
      isItemSelectable,
      keyField,
      onSelectionChange,
    } = this.props;

    // Check if the item is selectable
    if (isItemSelectable && !isItemSelectable(row)) {
      return;
    }

    const key = row[keyField];
    const { selection } = this.state;

    // start off with the existing state
    let newSelection = [...selection];

    const keyIndex = newSelection.indexOf(key);

    let isSelected;

    // check to see if the key exists
    if (keyIndex >= 0) {
      // it does exist so we will remove it using destructing
      newSelection = [
        ...newSelection.slice(0, keyIndex),
        ...newSelection.slice(keyIndex + 1),
      ];
      isSelected = false;
    } else {
      // it does not exist so add it
      newSelection.push(key);
      isSelected = true;
    }

    // update the state
    this.setState({ selection: newSelection });

    const items = this.mergeRecords(newSelection);

    dispatchChange(form, name, items.length > 0 ? items : null);

    if (onSelectionChange) {
      onSelectionChange({
        selectedIds: newSelection,
        items,
        row,
        isSelected,
        toggledId: key,
      });
    }
  };

  /**
   * Toggle all checkboxes for select table
   */
  toggleAll = () => {
    const {
      keyField,
      form,
      dispatchChange,
      name,
      isItemSelectable,
      onSelectionChange,
    } = this.props;

    const { selectAll } = this.state;

    const newSelectAll = !selectAll;

    const selection = [];

    if (newSelectAll) {
      // we need to get at the internals of ReactTable
      const wrappedInstance = this.checkboxTable.getWrappedInstance();
      // the 'sortedData' property contains the currently accessible records based on the filter and sort
      const currentRecords = wrappedInstance.getResolvedState().sortedData;
      // we just push all the IDs onto the selection array
      currentRecords.forEach((item) => {
        // Check if the item is selectable
        // eslint-disable-next-line no-underscore-dangle
        if (!isItemSelectable || isItemSelectable(item._original)) {
          // eslint-disable-next-line no-underscore-dangle
          selection.push(item._original[keyField]);
        }
      });
    }

    this.setState({ selectAll: newSelectAll, selection });

    const items = this.mergeRecords(selection);

    dispatchChange(form, name, items.length > 0 ? items : null);

    if (onSelectionChange) {
      onSelectionChange({
        selectedIds: selection,
        items,
        row: null,
        isSelected: newSelectAll,
        isToggleAll: true,
      });
    }
  };

  generateQueryToFetchData(state, whenPressEnterKey = true) {
    const { filtered } = state;

    const { columns } = this.props;

    // If "whenPressEnterKey" param is true, it means that
    // onFetch is executed when user presses Enter Key.
    // If it's false, it means that onFetch is executed when
    // it detects a change in filtered state only for Filters of type Select
    let validFetchData = whenPressEnterKey;

    // Check if there is a date filter
    // If there is, only send it when having 10 characters
    // TODO: Implement a Date Filter Component
    const query = filtered.reduce((filters, filter) => {
      const { id } = filter;

      let { value } = filter;

      const column = columns.find((columnElement) => {
        if (!columnElement.id) {
          return columnElement.accessor === id;
        }
        return columnElement.id === id;
      });

      let validFilter = true;

      if (value === null) {
        validFilter = false;
      }

      if (column.dataType) {
        switch (column.dataType) {
          case ColumnFilterDataTypes.ARRAY:
            // If array column filter, concatenate with |
            if (hasValidArrayColumnFilter(value)) {
              value = value.join('|');
              validFilter = true;
              validFetchData = true;
            } else {
              validFilter = false;
            }
            break;
          case ColumnFilterDataTypes.DATE:
            // If date column filter and has no valid format, don't include in query
            validFilter = hasValidDateColumnFilter(value);
            validFetchData = !!validFilter;
            break;
          default:
            validFilter = true;
        }
      }
      if (validFilter) {
        filters.push(`${id}:${value}`);
      }
      return filters;
    }, []);

    return { query, validFetchData };
  }

  mergeRecords = (selections = []) => {
    const {
      returnOnlySelectedItems,
      returnFullSelectedObjects,
      data,
      keyField,
    } = this.props;

    if (returnOnlySelectedItems) {
      // devolver objetos completos de los seleccionados
      if (returnFullSelectedObjects) {
        return selections.reduce((acc, key) => {
          const found = data.find((element) => element[keyField] === key);
          if (found) {
            acc.push(found);
          }
          return acc;
        }, []);
      }
      // devolver solo IDs (comportamiento original)
      return selections;
    }

    // if user want all id's with their states
    const { allElements } = this.state;

    const newAllElements = [...allElements];

    // reset all elements
    newAllElements.forEach((element, index) => {
      newAllElements[index].checked = false;
    });

    // activate selected id's
    selections.forEach((selection) => {
      const index = allElements.findIndex(
        (element) => element.id === selection,
      );

      if (index !== -1) {
        newAllElements[index].checked = true;
      }
    });

    return newAllElements;
  };

  /**
   * Whether or not a row is selected for select table
   */
  isSelected = (key) => {
    const { isItemSelectable, data, keyField } = this.props;

    // Find the full item
    const item = data.find((d) => d[keyField] === key);

    // If item is not selectable, return false
    if (isItemSelectable && !isItemSelectable(item)) {
      return false;
    }

    return this.state.selection.includes(key);
  };

  rowFn = (state, rowInfo) => {
    const { keyField, isItemSelectable } = this.props;

    const { selection } = this.state;

    const background =
      rowInfo &&
      selection.includes(rowInfo.original[keyField]) &&
      COLOR.selectedRow;

    return {
      onClick: (e, handleOriginal) => {
        if (handleOriginal) {
          handleOriginal();
        }
        // Check if the item is selectable before toggling
        else if (!isItemSelectable || isItemSelectable(rowInfo.original)) {
          // call toggleSelection function when row is clicked
          this.toggleSelection(rowInfo.original);
        }
      },
      style: {
        background,
      },
    };
  };

  rowFnDisabled = (state, rowInfo) => {
    const { keyField } = this.props;
    const { selection } = this.state;

    const background =
      rowInfo &&
      selection.includes(rowInfo.original[keyField]) &&
      COLOR.selectedRow;

    return {
      onClick: (e, handleOriginal) => {
        if (handleOriginal) {
          handleOriginal();
        }
        // No hacer nada cuando está deshabilitado
      },
      style: {
        background,
        opacity: 0.6,
        cursor: 'not-allowed',
      },
    };
  };

  /**
   * method public to unselect a item specific from outside
   */
  unselectItem = (itemKey) => {
    const { selection } = this.state;
    const { form, dispatchChange, name, keyField, onSelectionChange } =
      this.props;

    if (!selection.includes(itemKey)) {
      return;
    }

    // Remove the item from the selection
    const newSelection = selection.filter((key) => key !== itemKey);

    this.setState({ selection: newSelection });

    const items = this.mergeRecords(newSelection);
    dispatchChange(form, name, items.length > 0 ? items : null);

    const { data } = this.props;
    const row = data.find((d) => d[keyField] === itemKey);

    if (onSelectionChange) {
      onSelectionChange({
        selectedIds: newSelection,
        items,
        row,
        isSelected: false,
        toggledId: itemKey,
        programmatic: true,
      });
    }
  };

  /**
   * method public to select a item specific from outside
   */
  selectItem = (itemKey) => {
    const { selection } = this.state;
    const {
      form,
      dispatchChange,
      name,
      keyField,
      onSelectionChange,
      isItemSelectable,
      data,
    } = this.props;

    if (selection.includes(itemKey)) {
      return;
    }

    const row = data.find((d) => d[keyField] === itemKey);

    if (isItemSelectable && !isItemSelectable(row)) {
      return;
    }

    const newSelection = [...selection, itemKey];

    this.setState({ selection: newSelection });

    const items = this.mergeRecords(newSelection);
    dispatchChange(form, name, items.length > 0 ? items : null);

    if (onSelectionChange) {
      onSelectionChange({
        selectedIds: newSelection,
        items,
        row,
        isSelected: true,
        toggledId: itemKey,
        programmatic: true,
      });
    }
  };

  /**
   * method public to clear all selection
   */
  clearSelection = () => {
    const { form, dispatchChange, name } = this.props;

    this.setState({ selection: [], selectAll: false });
    dispatchChange(form, name, null);
  };

  render() {
    const {
      required,
      filterable,
      defaultPageSize,
      pages,
      name,
      defaultFilterMethod,
      isItemSelectable,
      showSelectAll,
      loading,
      disabled,
    } = this.props;

    const { selectAll } = this.state;

    const SelectInputComponent = (cellInfo) => {
      const { row } = cellInfo;

      const key = row[this.props.keyField];

      if (isItemSelectable && !isItemSelectable(row)) {
        return null;
      }

      return (
        <input
          type="checkbox"
          checked={this.isSelected(key)}
          disabled={disabled}
          onChange={() => {}}
          onClick={(e) => {
            if (disabled) {
              e.preventDefault();
              e.stopPropagation();
              return;
            }
            e.stopPropagation();
            this.toggleSelection(row);
          }}
        />
      );
    };

    const tableProps = {
      ...this.props,
      ref: (r) => {
        this.checkboxTable = r;
      },
      toggleSelection: this.toggleSelection,
      selectType: 'checkbox',
      isSelected: this.isSelected,
      getTrProps: disabled ? this.rowFnDisabled : this.rowFn,
      className: showSelectAll
        ? '-striped -highlight'
        : '-striped -highlight hide-select-all',
      SelectInputComponent,
      filterable,
      onFetchData: this.onFetchData,
      defaultPageSize,
      pages,
      defaultFilterMethod,
      loading,
      selectAll: false,
      toggleAll: () => {},
    };

    if (showSelectAll) {
      tableProps.selectAll = selectAll;
      tableProps.toggleAll = disabled ? () => {} : this.toggleAll;
    } else {
      tableProps.selectAll = false;
      tableProps.toggleAll = () => {};
    }

    return (
      <Fragment>
        <Field
          name={name}
          component={TextInput}
          type="hidden"
          validate={required ? [SelectionableTable.isRequired] : null}
        />
        <div
          ref={(r) => {
            this.wrapperRef = r;
          }}
        >
          <SelectionableTableHOC {...tableProps} />
        </div>
      </Fragment>
    );
  }
}

SelectionableTable.propTypes = {
  keyField: PropTypes.string,
  required: PropTypes.bool,
  dispatchChange: PropTypes.func.isRequired,
  form: PropTypes.string.isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ).isRequired,
  defaultPageSize: PropTypes.number,
  loading: PropTypes.bool,
  returnOnlySelectedItems: PropTypes.bool,
  activeField: PropTypes.string,
  filterable: PropTypes.bool,
  fetchData: PropTypes.func,
  params: PropTypes.shape({}),
  pages: PropTypes.number,
  name: PropTypes.string,
  defaultFilterMethod: PropTypes.func,
  shouldResetSelection: PropTypes.bool,
  isItemSelectable: PropTypes.func,
  queryProps: PropTypes.string,
  onSelectionChange: PropTypes.func,
  showSelectAll: PropTypes.bool,
  preSelectedIds: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.arrayOf(PropTypes.number),
  ]),
  disabled: PropTypes.bool,
  returnFullObject: PropTypes.bool,
  returnFullSelectedObjects: PropTypes.bool,
  blockEnterSubmit: PropTypes.bool,
};

SelectionableTable.defaultProps = {
  keyField: 'id',
  required: false,
  loading: false,
  returnOnlySelectedItems: false,
  activeField: 'active',
  filterable: false,
  fetchData: null,
  params: null,
  defaultPageSize: DEFAULT_PAGE_SIZE,
  pages: undefined,
  name: 'items',
  defaultFilterMethod: undefined,
  shouldResetSelection: false,
  isItemSelectable: null,
  queryProps: '',
  onSelectionChange: null,
  showSelectAll: true,
  preSelectedIds: null,
  disabled: false,
  returnFullObject: false,
  returnFullSelectedObjects: false,
  blockEnterSubmit: false,
};

const mapDispatchToProps = {
  dispatchChange: change,
};

export default connect(null, mapDispatchToProps, null, {
  forwardRef: true,
  withRef: true,
})(SelectionableTable);
